import { forwardRef, type InputHTMLAttributes, type LabelHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { cn } from '@/lib/utils';

const fieldClasses =
  'flex w-full rounded-md border border-input bg-card px-3 py-2 text-sm text-foreground shadow-sm transition-colors ' +
  'placeholder:text-muted-foreground ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-0 focus-visible:border-primary ' +
  'disabled:cursor-not-allowed disabled:opacity-50';

const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  ({ className, type, ...props }, ref) => (
    <input type={type} className={cn(fieldClasses, 'h-9', className)} ref={ref} {...props} />
  )
);
Input.displayName = 'Input';

const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(
  ({ className, ...props }, ref) => (
    <textarea className={cn(fieldClasses, 'min-h-[80px] resize-y', className)} ref={ref} {...props} />
  )
);
Textarea.displayName = 'Textarea';

const Label = forwardRef<HTMLLabelElement, LabelHTMLAttributes<HTMLLabelElement>>(
  ({ className, ...props }, ref) => (
    <label
      ref={ref}
      className={cn('block text-sm font-medium leading-none text-foreground peer-disabled:opacity-70', className)}
      {...props}
    />
  )
);
Label.displayName = 'Label';

/** Small helper text under a field. */
function FieldHint({ className, children }: { className?: string; children: React.ReactNode }) {
  return <p className={cn('mt-1.5 text-xs text-muted-foreground', className)}>{children}</p>;
}

export { Input, Textarea, Label, FieldHint, fieldClasses };
